import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { API_BASE_URL } from '../../config';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Shared/Sidebar';
import Header from '../Shared/Header';
import './Profile.css';

const Profile = () => {
    const { t, i18n } = useTranslation(['profile', 'common', 'navigation']);
    const navigate = useNavigate();

    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [boards, setBoards] = useState([]);
    const [classes, setClasses] = useState([]);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [isLangUpdating, setIsLangUpdating] = useState(false);
    const [langUpdateStatus, setLangUpdateStatus] = useState(null); // 'success' | 'error' | null
    const [userData, setUserData] = useState({
        name: 'Student',
        email: 'student@example.com',
        role: 'free',
        class: '10',
        board: 'CBSE',
        userId: localStorage.getItem('userId') || '0',
        classId: '',
        boardId: '',
        profileLanguage: 'en'
    });

    const [editData, setEditData] = useState({
        fullName: '',
        boardId: '',
        classId: '',
        profileLanguage: 'en'
    });


    const userId = userData.userId;

    useEffect(() => {
        // Load initial data from localStorage
        const userStr = localStorage.getItem('user');
        const user = userStr ? JSON.parse(userStr) : null;

        if (user) {
            setUserData(prev => ({
                ...prev,
                name: user.name || localStorage.getItem('studentName') || 'Student',
                email: user.email || 'student@example.com',
                role: user.role || 'free',
                class: user.class_name || localStorage.getItem('studentClass') || '10',
                board: localStorage.getItem('boardName') || 'CBSE',
                userId: user.userId || user.id || '0',
                chapter_count: user.chapter_count || 0,
                purchage_status: user.purchage_status || 'inactive',
                classId: user.class_id || '',
                boardId: user.board_id || '',
                profileLanguage: user.profile_language || 'en'
            }));
            if (user.profile_language) {
                i18n.changeLanguage(user.profile_language);
            }

        }

        // Fetch latest data from API to ensure sync
        const fetchProfile = async () => {
            if (!userId || userId === '0') {
                setLoading(false);
                return;
            }
            try {
                const res = await fetch(`${API_BASE_URL}get_user_profile.php?user_id=${userId}`, { credentials: 'include' });
                const data = await res.json();
                if (data.status === 'success') {
                    const latestUser = data.user;
                    setUserData(prev => ({
                        ...prev,
                        name: latestUser.name,
                        email: latestUser.email,
                        role: latestUser.role,
                        class: latestUser.class_name,
                        chapter_count: latestUser.chapter_count,
                        purchage_status: latestUser.purchage_status,
                        classId: latestUser.class_id,
                        boardId: latestUser.board_id,
                        profileLanguage: latestUser.profile_language || 'en'
                    }));
                    if (latestUser.profile_language) {
                        i18n.changeLanguage(latestUser.profile_language);
                    }

                    // Update localStorage
                    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                    localStorage.setItem('user', JSON.stringify({ ...currentUser, ...latestUser }));
                }
            } catch (err) {
                console.error("Profile sync error:", err);
            } finally {
                setLoading(false);
            }
        };

        const fetchBoards = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}get_boards.php`, { credentials: 'include' });
                const data = await res.json();
                setBoards(data);
            } catch (err) {
                console.error("Error fetching boards:", err);
            }
        };

        const fetchLanguages = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}get_languages.php`, { credentials: 'include' });
                const data = await res.json();
                if (data.status === 'success') {
                    setAvailableLanguages(data.languages);
                }
            } catch (err) {
                console.error("Error fetching languages:", err);
            }
        };

        fetchProfile();
        fetchBoards();
        fetchLanguages();
    }, [userId]);

    useEffect(() => {
        const fetchClasses = async () => {
            if (!editData.boardId) return;
            try {
                const res = await fetch(`${API_BASE_URL}get_classes.php?board_id=${editData.boardId}`, { credentials: 'include' });
                const data = await res.json();
                setClasses(data);
            } catch (err) {
                console.error("Error fetching classes:", err);
            }
        };
        fetchClasses();
    }, [editData.boardId]);

    const handleInstantLanguageChange = async (newLang) => {
        if (newLang === userData.profileLanguage) return;
        
        setIsLangUpdating(true);
        setLangUpdateStatus(null);
        
        try {
            const res = await fetch(`${API_BASE_URL}update_user_profile.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    full_name: userData.name,
                    board_id: userData.boardId,
                    class_id: userData.classId,
                    profile_language: newLang
                }),
                credentials: 'include'
            });
            const data = await res.json();
            
            if (data.status === 'success') {
                // Update i18n
                i18n.changeLanguage(newLang);
                
                // Update local state
                setUserData(prev => ({ ...prev, profileLanguage: newLang }));
                
                // Update localStorage
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                localStorage.setItem('user', JSON.stringify({ ...currentUser, profile_language: newLang }));
                
                setLangUpdateStatus('success');
                setTimeout(() => setLangUpdateStatus(null), 3000);
            } else {
                setLangUpdateStatus('error');
            }
        } catch (err) {
            console.error("Language update error:", err);
            setLangUpdateStatus('error');
        } finally {
            setIsLangUpdating(false);
        }
    };

    const handleLogout = () => {
        // Clear session data
        localStorage.removeItem('user');
        localStorage.removeItem('userId');
        localStorage.removeItem('studentName');
        localStorage.removeItem('studentClass');
        localStorage.removeItem('boardId');
        localStorage.removeItem('classId');
        localStorage.removeItem('boardName');
        navigate('/login');
    };

    const handleEditClick = () => {
        setEditData({
            fullName: userData.name,
            boardId: userData.boardId,
            classId: userData.classId,
            profileLanguage: userData.profileLanguage
        });

        setShowEditModal(true);
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setUpdating(true);
        try {
            const res = await fetch(`${API_BASE_URL}update_user_profile.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_id: userId,
                    full_name: editData.fullName,
                    board_id: editData.boardId,
                    class_id: editData.classId,
                    profile_language: editData.profileLanguage
                }),

                credentials: 'include'
            });
            const data = await res.json();
            if (data.status === 'success') {
                // Update local state and storage
                const selectedBoard = boards.find(b => b.id == editData.boardId);
                const selectedClass = classes.find(c => c.id == editData.classId);

                setUserData(prev => ({
                    ...prev,
                    name: editData.fullName,
                    board: selectedBoard ? selectedBoard.name : prev.board,
                    class: selectedClass ? selectedClass.class_name : prev.class,
                    boardId: editData.boardId,
                    classId: editData.classId,
                    profileLanguage: editData.profileLanguage
                }));

                i18n.changeLanguage(editData.profileLanguage);


                // Update localStorage
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                const updatedUser = {
                    ...currentUser,
                    name: editData.fullName,
                    full_name: editData.fullName,
                    board_id: editData.boardId,
                    class_id: editData.classId,
                    class_name: selectedClass ? selectedClass.class_name : currentUser.class_name
                };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                localStorage.setItem('studentName', editData.fullName);
                if (selectedClass) localStorage.setItem('studentClass', selectedClass.class_name);
                if (selectedBoard) localStorage.setItem('boardName', selectedBoard.name);

                setShowEditModal(false);
                alert(t('profile:profile.updateSuccess', 'Profile updated successfully!'));
            } else {
                alert(data.message || t('profile:profile.updateError', "Failed to update profile."));
            }
        } catch (err) {
            console.error("Update error:", err);
            alert(t('profile:profile.connectionError', "Connection error. Please try again."));
        } finally {

            setUpdating(false);
        }
    };

    return (
        <div className="dashboard-wrapper">
            <div className="dashboard-container">
                <Sidebar active="profile" />

                <main className="main-content-area">
                    <Header
                        title={`👤 ${t('profile:profile.title', 'Student Profile')}`}
                        subtitle={t('profile:profile.subtitle', 'Manage your account settings and preferences')}
                        showActions={true}
                        hidePill={true}
                    />


                    <div className="dashboard-scroll-container">
                        <div className="profile-page-content">
                            <div className="profile-layout">
                                {/* Left Side - User Summary */}
                                {loading ? (
                                    <div className="profile-user-card shimmer">
                                        <div className="profile-shimmer-placeholder avatar"></div>
                                        <div className="profile-shimmer-placeholder title"></div>
                                        <div className="profile-shimmer-placeholder badge"></div>
                                        <div className="profile-stats-mini">
                                            <div className="profile-shimmer-placeholder stat"></div>
                                            <div className="profile-shimmer-placeholder stat"></div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="profile-user-card">
                                        <div className="profile-avatar-large">
                                            {userData.name[0]}
                                        </div>
                                        <h2>{userData.name}</h2>
                                        <span className="role-badge">
                                            {userData.role === 'premium' ? t('profile:profile.premiumPlan', 'Premium User') :
                                                (userData.chapter_count > 0 && userData.purchage_status === 'active') ? t('profile:profile.chapterPlan', 'Chapter User') : t('profile:profile.freeLearner', 'Free User')}
                                        </span>


                                        <div className="profile-stats-mini">
                                            <div className="stat-item">
                                                <span className="stat-value">12</span>
                                                <span className="stat-label">{t('profile:profile.testsTaken', 'Tests Taken')}</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-value">85%</span>
                                                <span className="stat-label">{t('profile:profile.avgScore', 'Avg. Score')}</span>
                                            </div>

                                        </div>

                                        <div className="profile-actions" style={{ marginTop: '2.5rem', justifyContent: 'center' }}>
                                            <button className="logout-secondary-btn" onClick={() => setShowLogoutModal(true)}>
                                                🔒 {t('profile:profile.logoutSecurely', 'Logout Securely')}
                                            </button>
                                        </div>

                                    </div>
                                )}

                                {/* Right Side - Detailed Info */}
                                <div className="profile-details-section">
                                    {loading ? (
                                        <div className="info-group-card shimmer">
                                            <div className="profile-shimmer-placeholder header"></div>
                                            <div className="detail-grid">
                                                {[1, 2, 3, 4].map(i => (
                                                    <div key={i} className="profile-shimmer-placeholder detail-item shimmer-placeholder"></div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="info-group-card">
                                            <h3>🆔 {t('profile:profile.accountInfo', 'Account Information')}</h3>
                                            <div className="detail-grid">
                                                <div className="detail-item">
                                                    <span className="detail-label">{t('profile:profile.fullName', 'Full Name')}</span>
                                                    <div className="detail-value">{userData.name}</div>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">{t('profile:profile.email', 'Email Address')}</span>
                                                    <div className="detail-value">{userData.email}</div>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">{t('profile:profile.userId', 'User ID')}</span>
                                                    <div className="detail-value">#ST-{userData.userId}</div>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">{t('profile:profile.membership', 'Membership Type')}</span>
                                                    <div className="detail-value" style={{ textTransform: 'capitalize', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span>
                                                            {userData.role === 'premium' ? t('profile:profile.premiumPlan', 'Premium Plan') :
                                                                (userData.chapter_count > 0 && userData.purchage_status === 'active') ? t('profile:profile.chapterPlan', 'Chapter Plan') : t('profile:profile.freeLearner', 'Free Learner')}
                                                        </span>
                                                        <button 
                                                            className="link-btn-small" 
                                                            onClick={() => navigate('/subscriptions')}
                                                            style={{ fontSize: '0.8rem', color: 'var(--accent-blue)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 10px' }}
                                                        >
                                                            {t('profile:profile.viewHistory', 'View History')} 📋
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    )}

                                    {loading ? (
                                        <div className="info-group-card shimmer">
                                            <div className="profile-shimmer-placeholder header"></div>
                                            <div className="detail-grid">
                                                {[1, 2].map(i => (
                                                    <div key={i} className="profile-shimmer-placeholder detail-item shimmer-placeholder"></div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="info-group-card">
                                            <h3>🎓 {t('profile:profile.educationalSettings', 'Educational Settings')}</h3>
                                            <div className="detail-grid">
                                                <div className="detail-item">
                                                    <span className="detail-label">{t('profile:profile.board', 'Board of Education')}</span>
                                                    <div className="detail-value">{userData.board}</div>
                                                </div>
                                                <div className="detail-item">
                                                    <span className="detail-label">{t('profile:profile.class', 'Current Class')}</span>
                                                    <div className="detail-value"> {userData.class}</div>
                                                </div>
                                                 <div className="detail-item">
                                                     <span className="detail-label">{t('profile:profile.language', 'Profile Language')}</span>
                                                     <div className="detail-value"> {availableLanguages.find(l => l.code === userData.profileLanguage)?.name || userData.profileLanguage.toUpperCase()}</div>
                                                 </div>
                                            </div>
                                        </div>

                                    )}

                                    {loading ? (
                                        <div className="info-group-card shimmer">
                                            <div className="profile-shimmer-placeholder header"></div>
                                            <div className="lang-grid-shimmer">
                                                {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="lang-item-shimmer"></div>)}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="info-group-card">
                                            <div className="card-header-flex">
                                                <h3>🌐 {t('profile:profile.language', 'Language Preferences')}</h3>
                                                {isLangUpdating && <span className="updating-spinner">🔄</span>}
                                                {langUpdateStatus === 'success' && <span className="status-toast success">✅ {t('common:saved', 'Saved!')}</span>}
                                                {langUpdateStatus === 'error' && <span className="status-toast error">❌ {t('profile:profile.updateError', 'Error')}</span>}
                                            </div>
                                            <div className="language-selection-grid">
                                                {availableLanguages.map((lang) => (
                                                    <button 
                                                        key={lang.id} 
                                                        className={`lang-option-btn ${userData.profileLanguage === lang.code ? 'active' : ''}`}
                                                        onClick={() => handleInstantLanguageChange(lang.code)}
                                                        disabled={isLangUpdating}
                                                    >
                                                        <span className="lang-name">{lang.name}</span>
                                                        {userData.profileLanguage === lang.code && <span className="check-icon">✓</span>}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {!loading && (
                                        <div className="profile-actions">
                                            <button className="edit-profile-btn" onClick={handleEditClick}>
                                                ✏️ {t('profile:profile.updateProfile', 'Update Profile')}
                                            </button>
                                        </div>

                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            {/* Logout Confirmation Modal */}
            {showLogoutModal && ReactDOM.createPortal(
                <div className="logout-overlay" onClick={() => setShowLogoutModal(false)}>
                        <div className="logout-modal" onClick={e => e.stopPropagation()}>
                        <div className="logout-modal-icon">🚪</div>
                        <h3>{t('navigation:sidebar.logoutConfirmTitle', 'Log Out?')}</h3>
                        <p>{t('navigation:sidebar.logoutConfirmText', 'Are you sure you want to log out of SkillTherapy AI?')}</p>
                        <div className="logout-modal-actions">
                            <button className="logout-cancel-btn" onClick={() => setShowLogoutModal(false)}>
                                {t('common:cancel', 'Cancel')}
                            </button>
                            <button className="logout-confirm-btn" onClick={handleLogout}>
                                {t('navigation:sidebar.yesLogout', 'Yes, Log Out')}
                            </button>
                        </div>
                    </div>

                </div>,
                document.body
            )}

            {/* Edit Profile Modal */}
            {showEditModal && ReactDOM.createPortal(
                <div className="edit-modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="edit-modal" onClick={e => e.stopPropagation()}>
                        <div className="edit-modal-header">
                            <h2>✏️ {t('profile:profile.updateProfile', 'Update Profile')}</h2>
                            <p>{t('profile:profile.customizeInfo', 'Customize your personal and educational information.')}</p>
                        </div>

                        <form className="edit-form" onSubmit={handleUpdateProfile}>
                            <div className="form-group">
                                <label>{t('profile:profile.fullName', 'Full Name')}</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={editData.fullName}
                                    onChange={e => setEditData({ ...editData, fullName: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>{t('profile:profile.board', 'Board of Education')}</label>
                                <select
                                    className="form-select"
                                    value={editData.boardId}
                                    onChange={e => setEditData({ ...editData, boardId: e.target.value, classId: '' })}
                                    required
                                >
                                    <option value="">{t('common:select', 'Select Board')}</option>
                                    {boards.map(b => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>{t('profile:profile.class', 'Current Class')}</label>
                                <select
                                    className="form-select"
                                    value={editData.classId}
                                    onChange={e => setEditData({ ...editData, classId: e.target.value })}
                                    disabled={!editData.boardId}
                                    required
                                >
                                    <option value="">{t('common:select', 'Select Class')}</option>
                                    {classes.map(c => (
                                        <option key={c.id} value={c.id}>{c.class_name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>{t('profile:profile.language', 'Profile Language')}</label>
                                 <select
                                     className="form-select"
                                     value={editData.profileLanguage}
                                     onChange={e => setEditData({ ...editData, profileLanguage: e.target.value })}
                                     required
                                 >
                                     {availableLanguages.map(lang => (
                                         <option key={lang.id} value={lang.code}>{lang.name}</option>
                                     ))}
                                 </select>
                            </div>

                            <div className="edit-modal-actions">
                                <button type="button" className="cancel-btn" onClick={() => setShowEditModal(false)}>
                                    {t('common:cancel', 'Cancel')}
                                </button>
                                <button type="submit" className="save-btn" disabled={updating}>
                                    {updating ? t('profile:profile.saving', 'Saving...') : t('profile:profile.saveChanges', 'Save Changes')}
                                </button>
                            </div>

                        </form>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Profile;
